import React, { useState } from 'react';
import { Building2, Check, ChevronDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getActiveCompanyName } from '../../utils/tenant';
import { formatRole } from '../../utils/roleLabels';
import { isPlatformOwner } from '../../utils/platform';

type TenantSwitcherProps = {
  /** Compact footer / navbar control — only renders when the user can switch tenants. */
  variant?: 'default' | 'compact' | 'navbar';
};

export const TenantSwitcher: React.FC<TenantSwitcherProps> = ({ variant = 'default' }) => {
  const { user, memberships, activeCompanyId, membershipRole, switchTenant, hasActiveTenant } = useAuth();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState<string | null>(null);

  // Tenant-facing users must not switch workspaces; platform admins may still need context switching.
  if (!isPlatformOwner(user)) {
    return null;
  }

  if (!hasActiveTenant && memberships.length === 0) {
    return null;
  }

  const activeName = getActiveCompanyName(memberships, activeCompanyId) ?? 'Select workspace';
  const canSwitch = memberships.length > 1;

  const handleSwitch = async (companyId: string) => {
    if (companyId === activeCompanyId || switching) return;
    setSwitching(companyId);
    try {
      await switchTenant(companyId);
    } catch (error) {
      console.error('Failed to switch tenant:', error);
      setSwitching(null);
      setOpen(false);
    }
  };

  // Compact: only show interactive switcher when multiple tenants exist
  if (variant === 'compact' || variant === 'navbar') {
    if (!canSwitch) return null;
    const isNavbar = variant === 'navbar';
    return (
      <div className={`relative ${isNavbar ? '' : 'mt-1.5'}`}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={
            isNavbar
              ? 'flex max-w-full items-center gap-1.5 rounded-lg border border-[#E0DCE5] bg-white px-2.5 py-1.5 text-left text-xs font-medium text-[#261D30] transition hover:bg-[#F5F3F7]'
              : 'flex w-full items-center gap-1.5 rounded-md px-0.5 py-0.5 text-left text-[11px] text-white/55 transition hover:text-white/80'
          }
          aria-expanded={open}
          aria-label="Switch workspace"
        >
          <Building2 size={isNavbar ? 14 : 12} className="shrink-0 opacity-70" />
          <span className="min-w-0 flex-1 truncate">{isNavbar ? activeName : 'Switch workspace'}</span>
          <ChevronDown size={12} className={`shrink-0 transition ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
          <div
            className={
              isNavbar
                ? 'absolute right-0 top-full z-50 mt-1 w-64 overflow-hidden rounded-lg border border-[#E0DCE5] bg-white shadow-xl'
                : 'absolute bottom-full left-0 right-0 z-50 mb-1 overflow-hidden rounded-lg border border-white/10 bg-[#24102E] shadow-xl'
            }
          >
            {memberships.map((m) => {
              const isActive = m.companyId === activeCompanyId;
              return (
                <button
                  key={m.id}
                  type="button"
                  disabled={!!switching}
                  onClick={() => void handleSwitch(m.companyId)}
                  className={
                    isNavbar
                      ? `flex w-full items-center gap-2 px-3 py-2.5 text-left text-xs transition hover:bg-[#F5F3F7] ${
                          isActive ? 'bg-[#F5F3F7] text-[#261D30]' : 'text-[#73677E]'
                        }`
                      : `flex w-full items-center gap-2 px-2.5 py-2 text-left text-xs transition hover:bg-white/10 ${
                          isActive ? 'bg-white/10 text-white' : 'text-white/75'
                        }`
                  }
                >
                  <span className="min-w-0 flex-1 truncate">{m.companyName}</span>
                  <span className={`text-[10px] ${isNavbar ? 'text-[#9C84A4]' : 'text-white/45'}`}>
                    {formatRole(m.role)}
                  </span>
                  {isActive && <Check size={12} className="shrink-0 text-emerald-500" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  if (!canSwitch) {
    return (
      <div className="px-3 py-2">
        <div className="flex items-center gap-2 rounded-lg bg-white/5 px-3 py-2 text-sm text-white/90">
          <Building2 size={16} className="shrink-0 text-white/70" />
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium">{activeName}</p>
            {membershipRole && (
              <p className="truncate text-xs text-white/60">{formatRole(membershipRole)}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative px-3 py-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 rounded-lg bg-white/5 px-3 py-2 text-left text-sm text-white/90 transition hover:bg-white/10"
      >
        <Building2 size={16} className="shrink-0 text-white/70" />
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium">{activeName}</p>
          {membershipRole && (
            <p className="truncate text-xs text-white/60">{formatRole(membershipRole)}</p>
          )}
        </div>
        <ChevronDown size={16} className={`shrink-0 transition ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute left-3 right-3 top-full z-50 mt-1 overflow-hidden rounded-lg border border-white/10 bg-[#2D1537] shadow-xl">
          {memberships.map((m) => {
            const isActive = m.companyId === activeCompanyId;
            return (
              <button
                key={m.id}
                type="button"
                disabled={!!switching}
                onClick={() => void handleSwitch(m.companyId)}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition hover:bg-white/10 ${
                  isActive ? 'bg-white/10 text-white' : 'text-white/80'
                }`}
              >
                <span className="min-w-0 flex-1 truncate">{m.companyName}</span>
                <span className="text-xs text-white/50">{formatRole(m.role)}</span>
                {isActive && <Check size={14} className="shrink-0 text-emerald-400" />}
                {switching === m.companyId && <span className="text-xs text-white/50">…</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TenantSwitcher;
